import type { Ref } from 'vue'
import { getLoginPath } from '@/composables/auth-redirect'
import { useFeedActions, type FeedActionKind } from '@/composables/feed/use-feed-actions'
import type { ListingPreviewData } from '@/composables/listing/use-listing-preview'
import { useClient } from '@/stores/client'

const RESPONSE_ACTIONS: FeedActionKind[] = ['primary', 'question', 'interview', 'offer']

/**
 * Who may do what on a vacancy or resume opened outside the feed (its page and
 * the preview over lists). The feed actions do the work; this adds the owner,
 * the guest, an unpublished listing and the response the listing was opened from.
 */
export function useListingActions(
  data: Readonly<Ref<ListingPreviewData | null>>,
  responseId: Readonly<Ref<string | null | undefined>>,
  showDetails: () => void,
) {
  const client = useClient()
  const route = useRoute()
  const actions = useFeedActions()
  const responseUpdate = useState<{ id: string, status: number } | null>('response-list-update', () => null)

  const item = computed(() => data.value?.item ?? null)

  watch(data, (value) => {
    if (value === null) return
    actions.favoriteState.set(value.item.id, value.favoriteId)
    actions.responseState.set(value.item.id, value.responseId)
  }, { immediate: true })

  const isOwner = computed(() => {
    const current = item.value
    if (current === null || !client.isAuthenticated) return false
    return current.type === 'vacancy'
      ? client.companies.some(company => String(company.id) === current.vacancy.company.id)
      : current.summary.candidate.id === client.getClientId
  })
  const favoriteActive = computed(() => item.value ? actions.isFavorite(item.value) : false)
  const hasResponse = computed(() => item.value ? actions.hasResponse(item.value) : false)
  /** The main CTA is shown: a response opens its actions, a guest is sent to sign in. */
  const canAct = computed(() => {
    if (responseId.value) return true
    if (item.value === null || isOwner.value || data.value?.unpublished) return false
    if (!client.isAuthenticated) return true
    return item.value.type === 'vacancy' ? !client.getActiveCompany : !!client.getActiveCompany
  })
  /** Label of the CTA under the details; null hides it. */
  const primaryLabel = computed(() => {
    if (responseId.value || !canAct.value || item.value === null) return null
    if (item.value.type === 'summary') return 'Пригласить'
    return hasResponse.value ? 'Вы откликнулись' : 'Откликнуться'
  })

  async function onAction(kind: FeedActionKind) {
    const current = item.value
    if (current === null) return
    if (RESPONSE_ACTIONS.includes(kind)) {
      if (!canAct.value) return
      // The response decides what happens next: its actions live in the details.
      if (responseId.value) {
        showDetails()
        return
      }
    }
    if (!client.isAuthenticated && kind !== 'more') {
      await navigateTo(getLoginPath(route.fullPath))
      return
    }
    await actions.dispatch(kind, current)
  }

  async function onResponded(payload: { itemId: string, responseId: string }) {
    if (item.value?.id === payload.itemId) await actions.respondWithSummary(item.value, payload.responseId)
  }

  /** Lets the response list underneath refresh the status without a reload. */
  function onResponseChanged(status: number) {
    if (responseId.value) responseUpdate.value = { id: responseId.value, status }
  }

  return { actions, item, isOwner, favoriteActive, hasResponse, canAct, primaryLabel, onAction, onResponded, onResponseChanged }
}
